import moment from "moment";
import type { ReactNode } from "react";
import { useParams } from "react-router";
import MainLayout from "@/components/layouts";
import { ActivityLog } from "@/components/shared/activity-log";
import { PLACEHOLDER_IMAGE } from "@/components/shared/image-fallback";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import AppointmentStatusBadge from "@/app/pmc/appointments/components/AppointmentStatusBadge";
import { appointment_status } from "@/models/appointment.model";
import { FINISHED_OR_COMPLETED } from "@/repositories/appointment-records";
import type { AppointmentJoin } from "@/repositories/appointments.repository";
import { assigneeOf } from "./appointments-table";
import { useContactAppointment } from "./contact.hooks";
import { EvidencePhotos } from "./evidence-photos";

const when = (value: string | undefined) => (value ? moment(value).format("MM/DD/YYYY, h:mm a") : null);

const scheduledOn = ({ date, time }: AppointmentJoin) => [moment(date).format("MM/DD/YYYY"), time && moment(time, "HH:mm:ss").format("h:mm a")].filter(Boolean).join(", ");

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-1">{label}</h3>
      <div className="text-base font-medium leading-snug">{children}</div>
    </div>
  );
}

function Overview({ appointment }: { appointment: AppointmentJoin }) {
  const { property, meta_data: meta } = appointment;
  const finishedAt = FINISHED_OR_COMPLETED.includes(appointment.status) ? when(meta?.done_at) : null;
  const cancellation = appointment.status === appointment_status.cancelled ? meta : undefined;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="uppercase">Overview</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-4">
          <Avatar className="w-16 h-16 rounded-md flex-shrink-0">
            <AvatarImage src={property.image ?? PLACEHOLDER_IMAGE} alt={property.name} />
            <AvatarFallback className="rounded-md">{property.name[0]}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">{property.name}</h2>
            {property.address && <p className="text-sm text-muted-foreground">{property.address}</p>}
            {property.unit_number && <p className="text-sm text-muted-foreground">Unit: {property.unit_number}</p>}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Info label="Status">
            <AppointmentStatusBadge status={appointment.status} />
          </Info>
          <Info label="Service">
            {appointment.service.name}
            {appointment.service.parent_name && <p className="text-muted-foreground text-sm">{appointment.service.parent_name}</p>}
          </Info>
          <Info label="Vendor">{appointment.vendor.name}</Info>
          <Info label="Assigned To">{assigneeOf(appointment)}</Info>
          <Info label="Scheduled Date">{scheduledOn(appointment)}</Info>
          <Info label="Created Date">{moment(appointment.created_at).format("MM/DD/YYYY")}</Info>
          {finishedAt && <Info label="Done At">{finishedAt}</Info>}
          {appointment.work_order_notes && <Info label="Work Order Notes">{appointment.work_order_notes}</Info>}
        </div>

        {cancellation && (
          <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-md p-4 space-y-2">
            <h3 className="text-sm font-semibold text-red-800 dark:text-red-200 uppercase">Cancellation Details</h3>
            {cancellation.cancellation_notes && <p className="text-sm text-red-900 dark:text-red-100">{cancellation.cancellation_notes}</p>}
            {appointment.cancelled_by_user?.name && <p className="text-sm text-red-900 dark:text-red-100">Cancelled by {appointment.cancelled_by_user.name}</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

type Crumb = { name: string; href: string };

const APPOINTMENTS: Crumb = { name: "Appointments", href: "/appointments" };

export default function ContactAppointmentPage({ back = APPOINTMENTS }: { back?: Crumb }) {
  const { appointmentId: id = "" } = useParams();
  const { value, loading, notFound, error } = useContactAppointment(id);
  const title = `Booking #${id.slice(0, 8)}`;
  const breadcrumb = [
    back,
    { name: title, href: "#" },
  ];

  if (loading)
    return (
      <MainLayout breadcrumb={breadcrumb}>
        <div className="space-y-6">
          <Skeleton className="h-64 w-full rounded-lg" />
          <Skeleton className="h-48 w-full rounded-lg" />
        </div>
      </MainLayout>
    );

  if (!value)
    return (
      <MainLayout breadcrumb={breadcrumb}>
        <p className="text-destructive">{notFound ? "Appointment not found" : error}</p>
      </MainLayout>
    );

  return (
    <MainLayout breadcrumb={breadcrumb} pageHeader={{ title, subtitle: "Appointment details and documentation" }}>
      <div className="container mx-auto space-y-6">
        <Overview appointment={value.appointment} />
        <Card>
          <CardHeader>
            <CardTitle className="uppercase">Photo Documentation</CardTitle>
          </CardHeader>
          <CardContent>
            <EvidencePhotos photos={value.photos} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="uppercase">Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <ActivityLog logs={value.history} />
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
